import { useMemo } from "react";
import { Message as AIMessage } from "ai";
import { CustomComponent, TOOL_TYPES } from "./ImproveChat.model";
import { renderCustomComponent } from "./componentRenderers";
import styles from "./ImproveChat.module.scss";
import { ChainOfThoughtStep } from "@/components/ai-elements/chain-of-thought-for-improve-chat";
import { Message, MessageContent, MessageResponse } from "@/components/ai-elements/message";
import {DotIcon, GripIcon } from "lucide-react";

interface ImproveChatMessageProps {
  message: AIMessage;
  isLast: boolean;
  isDisabled?: boolean;
}

export function ImproveChatMessage({
  message,
  isLast,
  isDisabled = false,
}: ImproveChatMessageProps) {
  const isUser = message.role === "user";

  const customComponents = useMemo(() => {
    try {
      const components: JSX.Element[] = [];

      // Process tool invocations
      const toolInvocations = message.toolInvocations
      if (toolInvocations && toolInvocations.length > 0) {
        toolInvocations.forEach((toolInvocation, index) => {
          try {
            const { toolName, args } = toolInvocation;

            if (!toolName || !args) {
              return;
            }

            let customComponent: CustomComponent | null = null;

            // Transform to CustomComponent based on tool type
            if (
              toolName === TOOL_TYPES.SUGGEST_BRIEF_EDIT ||
              toolName === TOOL_TYPES.SUGGEST_INSTRUCTION_ADD ||
              toolName === TOOL_TYPES.SUGGEST_PREREQUISITE_ADD
            ) {
              customComponent = {
                type: "tool-display",
                data: {
                  toolName,
                  args,
                },
              };
            }

            if (customComponent) {
              components.push(
                <div key={`tool-${toolName}-${index}`}>
                  {renderCustomComponent(customComponent, isDisabled)}
                </div>
              );
            }
          } catch (e) {
            console.error("Error rendering tool invocation:", e);
          }
        });
      }


      return components.length > 0 ? <>{components}</> : null;
    } catch (e) {
      console.error("Error parsing custom components:", e);
      return null;
    }
  }, [message.toolInvocations, message.annotations, isDisabled]);

  const hasText = message.content.trim().length > 0;
  const shouldRender = hasText || Boolean(customComponents);

  if (!shouldRender) {
    return null;
  }

  return (
    <ChainOfThoughtStep
      icon={isUser ? DotIcon : GripIcon}
      label={isUser ? "" : ""}
      status={isLast ? "active" : "complete"}
      variant="bubble"
      role={isUser ? "user" : "assistant"}
      style={{ width: "100%" }}
    >
      <Message from={isUser ? "user" : "assistant"}>
        <MessageContent className="group-[.is-user]:bg-foreground group-[.is-user]:text-background">
          {hasText && (
            <MessageResponse className={styles.messageText}>
                {message.content}
            </MessageResponse>
          )}
          {customComponents && (
            <div className={styles.toolCards}>{customComponents}</div>
          )}
        </MessageContent>
      </Message>
    </ChainOfThoughtStep>
  );
}
